import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import toast from "react-hot-toast";
import {
  getOccasionsByEventId,
  getTrackedInvites,
  updateOccasion,
} from "../../server/Api";
import { getMainGuestsAndAttendees } from "../../utils/getGuestWithAttendees";
import { fetchAllocations } from "../../utils/getAllocatedGuestAndAttendees";
import {
  SEATING_STYLES,
  getSeatingStyle,
  stylesForAllocation,
} from "./seatingStyles";
import CustomSeatingBuilder from "./CustomSeatingBuilder";
import {
  parseCapacity,
  computeSeatLayout,
  seatLayoutCapacityNotice,
  capacityApiMessage,
} from "../../utils/capacityLimits";
import CapacityNoticeModal from "../../components/CapacityNoticeModal";
import "../../assets/css/SeatAllocationBoard.css";
import "../../assets/css/SeatLayoutStudio.css";

const sameId = (a, b) => a != null && b != null && String(a) === String(b);
const toNum = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
};
const idStr = (value) => (value == null ? "" : String(value));

const SeatLayoutStudio = () => {
  const selectedEvent = useSelector((state) => state.auth.selectedEvent);
  const eventEntity = useSelector((state) => state.auth.user);
  const isViewer = eventEntity?.role === "Viewer";

  const [occasions, setOccasions] = useState([]);
  const [occasionId, setOccasionId] = useState("");
  const [guestWithAttendees, setGuestWithAttendees] = useState([]);
  const [selectedAllocations, setSelectedAllocations] = useState({});
  const [rsvpMap, setRsvpMap] = useState({});
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editingStyle, setEditingStyle] = useState(null);
  const [capacityNotice, setCapacityNotice] = useState(null);
  const [saving, setSaving] = useState(false);
  const [capacityForm, setCapacityForm] = useState({
    numberOfRows: 0,
    numberOfSeatsPerRow: 0,
    numberOfTables: 0,
    numberOfSeatsPerTable: 0,
    seatAllocationType: "both",
  });

  const occasion = useMemo(
    () => occasions.find((o) => sameId(o._id, occasionId)) || null,
    [occasions, occasionId]
  );

  const allocationType = String(occasion?.seatAllocationType || "both")
    .toLowerCase()
    .trim();

  const customSeatingStyles = useMemo(
    () =>
      Array.isArray(occasion?.customSeatingStyles)
        ? occasion.customSeatingStyles
        : [],
    [occasion]
  );

  const occasionCapacity = parseCapacity(occasion?.OccasionCapacity);
  const liveSeatLayout = useMemo(
    () => computeSeatLayout(capacityForm),
    [capacityForm]
  );
  const seatsOverCapacity =
    occasionCapacity != null && liveSeatLayout.totalSeats > occasionCapacity;
  const activeSeatingStyleId = occasion?.activeSeatingStyleId || "classic";

  const layoutMeta = useMemo(() => {
    if (!occasion) return null;
    return {
      rows: Math.max(toNum(occasion.numberOfRows), 1),
      seatsPerRow: Math.max(toNum(occasion.numberOfSeatsPerRow), 1),
      tables: Math.max(toNum(occasion.numberOfTables), 1),
      seatsPerTable: Math.max(toNum(occasion.numberOfSeatsPerTable), 1),
    };
  }, [occasion]);

  const peopleCount = useMemo(() => {
    if (!occasionId) return 0;
    const oid = String(occasionId);
    const rsvpRequired = Boolean(occasion?.RSVP);
    let count = 0;
    (guestWithAttendees || []).forEach((entry) => {
      const guest = entry.guest || {};
      const guestId = idStr(guest._id);
      if (!guestId) return;
      const mainKey = `${guestId}-null`;
      const mainOn = (selectedAllocations[mainKey] || []).some((id) =>
        sameId(id, oid)
      );
      const mainOk =
        !rsvpRequired ||
        String(rsvpMap[mainKey]?.[oid] || "").toLowerCase() === "yes";
      if (mainOn && mainOk) count += 1;
      (entry.attendees || []).forEach((att) => {
        const attendeeId = idStr(att._id);
        if (!attendeeId) return;
        const key = `${guestId}-${attendeeId}`;
        const on = (selectedAllocations[key] || []).some((id) =>
          sameId(id, oid)
        );
        const ok =
          !rsvpRequired ||
          String(rsvpMap[key]?.[oid] || "").toLowerCase() === "yes";
        if (on && ok) count += 1;
      });
    });
    return count;
  }, [
    occasionId,
    occasion?.RSVP,
    guestWithAttendees,
    selectedAllocations,
    rsvpMap,
  ]);

  const catalog = useMemo(
    () => stylesForAllocation(allocationType || "both", customSeatingStyles),
    [allocationType, customSeatingStyles]
  );

  useEffect(() => {
    const load = async () => {
      if (!selectedEvent?.eventId) return;
      try {
        const [occRes, guests] = await Promise.all([
          axios.get(getOccasionsByEventId(selectedEvent.eventId)),
          getMainGuestsAndAttendees(selectedEvent.eventId),
        ]);
        const occList = occRes.data?.data || [];
        setOccasions(occList);
        setGuestWithAttendees(guests || []);
        if (occList.length) {
          setOccasionId((prev) => prev || String(occList[0]._id));
        }
      } catch (err) {
        console.error(err);
        toast.error("Failed to load layout studio.");
      }
      try {
        const { formatted } = await fetchAllocations(selectedEvent.eventId);
        setSelectedAllocations(formatted || {});
      } catch (err) {
        console.error(err);
      }
    };
    load();
  }, [selectedEvent?.eventId]);

  useEffect(() => {
    if (!occasion) return;
    const type = String(occasion.seatAllocationType || "both")
      .toLowerCase()
      .trim();
    setCapacityForm({
      numberOfRows: toNum(occasion.numberOfRows),
      numberOfSeatsPerRow: toNum(occasion.numberOfSeatsPerRow),
      numberOfTables: toNum(occasion.numberOfTables),
      numberOfSeatsPerTable: toNum(occasion.numberOfSeatsPerTable),
      seatAllocationType:
        type === "row" || type === "table" || type === "both" ? type : "both",
    });
  }, [occasion]);

  useEffect(() => {
    const loadRsvp = async () => {
      if (!selectedEvent?.eventId || !occasionId) return;
      try {
        const res = await axios.get(getTrackedInvites(selectedEvent.eventId));
        const map = {};
        (res.data?.data || []).forEach((item) => {
          const guestId = idStr(item.guestId);
          const attendeeId = item.attendeeId ? idStr(item.attendeeId) : "null";
          const key = `${guestId}-${attendeeId}`;
          const oid = idStr(item.occasionId);
          if (!map[key]) map[key] = {};
          map[key][oid] = item.response;
        });
        setRsvpMap(map);
      } catch (err) {
        console.error(err);
      }
    };
    loadRsvp();
  }, [selectedEvent?.eventId, occasionId]);

  const patchOccasion = async (payload, successMsg) => {
    if (!occasionId) return null;
    setSaving(true);
    try {
      const res = await axios.put(updateOccasion(occasionId), payload);
      const updated = res.data?.data || res.data;
      setOccasions((prev) =>
        prev.map((o) =>
          sameId(o._id, occasionId) ? { ...o, ...payload, ...updated } : o
        )
      );
      if (successMsg) toast.success(successMsg);
      return updated;
    } catch (err) {
      console.error(err);
      if (err?.response?.data?.code === "CAPACITY_EXCEEDED") {
        setCapacityNotice({
          title: "Cannot exceed occasion capacity",
          message: capacityApiMessage(err),
        });
      } else {
        toast.error(err?.response?.data?.message || "Failed to save.");
      }
      return null;
    } finally {
      setSaving(false);
    }
  };

  const saveCapacity = async () => {
    if (isViewer) return;
    const payload = {
      numberOfRows: Math.max(0, Number(capacityForm.numberOfRows) || 0),
      numberOfSeatsPerRow: Math.max(
        0,
        Number(capacityForm.numberOfSeatsPerRow) || 0
      ),
      numberOfTables: Math.max(0, Number(capacityForm.numberOfTables) || 0),
      numberOfSeatsPerTable: Math.max(
        0,
        Number(capacityForm.numberOfSeatsPerTable) || 0
      ),
      seatAllocationType: capacityForm.seatAllocationType || "both",
    };
    const occasionCapacity = parseCapacity(occasion?.OccasionCapacity);
    const seatLayout = computeSeatLayout(payload);
    if (occasionCapacity != null && seatLayout.totalSeats > occasionCapacity) {
      setCapacityNotice(
        seatLayoutCapacityNotice({
          occasionCapacity,
          ...seatLayout,
        })
      );
      return;
    }
    await patchOccasion(payload, "Capacity updated.");
  };

  const setActiveStyle = async (styleId) => {
    if (isViewer) return;
    await patchOccasion(
      { activeSeatingStyleId: styleId },
      "Layout selected for the seating board."
    );
  };

  const persistCustomStyles = async (nextStyles, selectId) => {
    const payload = { customSeatingStyles: nextStyles };
    if (selectId) payload.activeSeatingStyleId = selectId;
    const ok = await patchOccasion(
      payload,
      selectId && nextStyles.find((s) => s.id === selectId)?.status === "draft"
        ? "Draft layout saved."
        : "Custom layout saved."
    );
    if (ok) {
      setBuilderOpen(false);
      setEditingStyle(null);
    }
  };

  const handleSaveCustom = async (style, options = {}) => {
    const asDraft = Boolean(options.asDraft) || style.status === "draft";
    const { _rsvpShortfall, ...rest } = style;
    const clean = {
      ...rest,
      status: asDraft ? "draft" : "published",
      tag: asDraft ? "Draft" : "Custom",
    };
    const without = customSeatingStyles.filter((s) => s?.id !== clean.id);
    if (_rsvpShortfall > 0 && !asDraft) {
      toast(
        `Saved with ${_rsvpShortfall} fewer seats than RSVP’d guests.`,
        { icon: "⚠️" }
      );
    }
    await persistCustomStyles([...without, clean], clean.id);
  };

  const handleDeleteCustom = async (styleId) => {
    if (isViewer) return;
    const next = customSeatingStyles.filter((s) => s?.id !== styleId);
    const payload = { customSeatingStyles: next };
    if (activeSeatingStyleId === styleId) {
      payload.activeSeatingStyleId = "classic";
    }
    await patchOccasion(payload, "Custom layout deleted.");
  };

  return (
    <div className="main home-section seat-allocation-page sls-page">
      <div className="container-fluid py-3">
        <div className="sls-toolbar">
          <h4 className="sab-page-title mb-0">Seating Layouts</h4>
          <div className="sls-toolbar-actions">
            <div className="sls-occasion-field">
              <label className="sab-control-label" htmlFor="sls-occasion">
                Event Occasion
              </label>
              <select
                id="sls-occasion"
                className="form-select sab-control-select"
                value={occasionId}
                onChange={(e) => setOccasionId(e.target.value)}
              >
                <option value="">Select Occasion</option>
                {occasions.map((item) => (
                  <option key={item._id} value={item._id}>
                    {item.OccasionName}
                  </option>
                ))}
              </select>
            </div>
            <Link to="/seat-allocation/board" className="btn sab-manage-btn">
              Open seating board
            </Link>
          </div>
        </div>

        {!occasionId ? (
          <div className="sab-empty-state">
            <h5>Select an occasion</h5>
            <p>Choose an occasion to manage seating layouts.</p>
          </div>
        ) : (
          <>
            <section className="sls-capacity card-like">
              <div className="sab-style-picker-head">
                <div>
                  <span className="sab-style-picker-kicker">Capacity</span>
                  <strong>Rows &amp; tables for this occasion</strong>
                </div>
              </div>
              <div className="sls-capacity-row">
                <div className="sab-capacity-grid">
                  <label className="sab-capacity-field is-setup-type">
                    <span>Setup type</span>
                    <select
                      className="form-select sab-capacity-select"
                      disabled={isViewer}
                      value={capacityForm.seatAllocationType || "both"}
                      onChange={(e) =>
                        setCapacityForm((p) => {
                          const nextType = e.target.value;
                          const next = { ...p, seatAllocationType: nextType };
                          if (nextType === "row") {
                            next.numberOfTables = 0;
                            next.numberOfSeatsPerTable = 0;
                          } else if (nextType === "table") {
                            next.numberOfRows = 0;
                            next.numberOfSeatsPerRow = 0;
                          }
                          return next;
                        })
                      }
                    >
                      <option value="row">Rows only</option>
                      <option value="table">Tables only</option>
                      <option value="both">Hybrid (rows + tables)</option>
                    </select>
                  </label>
                  {(capacityForm.seatAllocationType === "row" ||
                    capacityForm.seatAllocationType === "both") && (
                    <>
                  <label className="sab-capacity-field">
                    <span>Number of rows</span>
                    <input
                      type="number"
                      min="0"
                      className="form-control"
                      disabled={isViewer}
                      value={capacityForm.numberOfRows}
                      onChange={(e) =>
                        setCapacityForm((p) => ({
                          ...p,
                          numberOfRows: e.target.value,
                        }))
                      }
                    />
                  </label>
                  <label className="sab-capacity-field">
                    <span>Default seats / row</span>
                    <input
                      type="number"
                      min="0"
                      className="form-control"
                      disabled={isViewer}
                      value={capacityForm.numberOfSeatsPerRow}
                      onChange={(e) =>
                        setCapacityForm((p) => ({
                          ...p,
                          numberOfSeatsPerRow: e.target.value,
                        }))
                      }
                    />
                  </label>
                    </>
                  )}
                  {(capacityForm.seatAllocationType === "table" ||
                    capacityForm.seatAllocationType === "both") && (
                    <>
                  <label className="sab-capacity-field">
                    <span>Number of tables</span>
                    <input
                      type="number"
                      min="0"
                      className="form-control"
                      disabled={isViewer}
                      value={capacityForm.numberOfTables}
                      onChange={(e) =>
                        setCapacityForm((p) => ({
                          ...p,
                          numberOfTables: e.target.value,
                        }))
                      }
                    />
                  </label>
                  <label className="sab-capacity-field">
                    <span>Default seats / table</span>
                    <input
                      type="number"
                      min="0"
                      className="form-control"
                      disabled={isViewer}
                      value={capacityForm.numberOfSeatsPerTable}
                      onChange={(e) =>
                        setCapacityForm((p) => ({
                          ...p,
                          numberOfSeatsPerTable: e.target.value,
                        }))
                      }
                    />
                  </label>
                    </>
                  )}
                </div>
                {occasionCapacity != null || liveSeatLayout.totalSeats ? (
                  <div
                    className={`sls-capacity-hint${
                      seatsOverCapacity ? " is-over" : ""
                    }`}
                  >
                    <div className="sls-capacity-hint-title">Seat summary</div>
                    <div className="sls-capacity-hint-grid">
                      <div className="sls-capacity-hint-stat">
                        <span>Total seats</span>
                        <strong>{liveSeatLayout.totalSeats}</strong>
                      </div>
                      {occasionCapacity != null ? (
                        <div className="sls-capacity-hint-stat">
                          <span>Occasion capacity</span>
                          <strong>
                            {occasionCapacity}
                            <em>
                              {seatsOverCapacity
                                ? `· ${
                                    liveSeatLayout.totalSeats - occasionCapacity
                                  } over`
                                : `· remaining ${Math.max(
                                    0,
                                    occasionCapacity - liveSeatLayout.totalSeats
                                  )}`}
                            </em>
                          </strong>
                        </div>
                      ) : null}
                    </div>
                    <div className="sls-capacity-hint-split">
                      {liveSeatLayout.type !== "table" ? (
                        <div className="sls-capacity-hint-stat">
                          <span>Row seats</span>
                          <strong>
                            {liveSeatLayout.rowSeats}
                            {capacityForm.numberOfRows &&
                            capacityForm.numberOfSeatsPerRow ? (
                              <em>
                                ({capacityForm.numberOfRows} ×{" "}
                                {capacityForm.numberOfSeatsPerRow})
                              </em>
                            ) : null}
                          </strong>
                        </div>
                      ) : null}
                      {liveSeatLayout.type !== "row" ? (
                        <div className="sls-capacity-hint-stat">
                          <span>Table seats</span>
                          <strong>
                            {liveSeatLayout.tableSeats}
                            {capacityForm.numberOfTables &&
                            capacityForm.numberOfSeatsPerTable ? (
                              <em>
                                ({capacityForm.numberOfTables} ×{" "}
                                {capacityForm.numberOfSeatsPerTable})
                              </em>
                            ) : null}
                          </strong>
                        </div>
                      ) : null}
                    </div>
                  </div>
                ) : null}
                {!isViewer ? (
                  <button
                    type="button"
                    className="btn btn-primary sab-save-btn sls-capacity-save"
                    disabled={saving}
                    onClick={saveCapacity}
                  >
                    {saving ? "Saving…" : "Save capacity"}
                  </button>
                ) : null}
              </div>
            </section>

            {builderOpen ? (
              <CustomSeatingBuilder
                initialStyle={editingStyle}
                layoutMeta={layoutMeta}
                rsvpPeopleCount={peopleCount}
                saving={saving}
                onCancel={() => {
                  setBuilderOpen(false);
                  setEditingStyle(null);
                }}
                onSave={(style) => handleSaveCustom(style, { asDraft: false })}
                onSaveDraft={(style) =>
                  handleSaveCustom(style, { asDraft: true })
                }
              />
            ) : (
              <section className="sls-catalog card-like">
                <div className="sab-style-picker-head">
                  <div>
                    <span className="sab-style-picker-kicker">
                      Advanced layouts
                    </span>
                    <strong>All presets &amp; custom styles</strong>
                    <p className="sab-custom-sub mb-0">
                      Active on board:{" "}
                      <em>
                        {
                          getSeatingStyle(
                            activeSeatingStyleId,
                            customSeatingStyles
                          ).name
                        }
                      </em>
                    </p>
                  </div>
                  {!isViewer ? (
                    <button
                      type="button"
                      className="btn sab-style-create-btn"
                      onClick={() => {
                        setEditingStyle(null);
                        setBuilderOpen(true);
                      }}
                    >
                      + Create custom
                    </button>
                  ) : null}
                </div>

                <div className="sab-style-grid">
                  {catalog.map((style) => {
                    const isActive = activeSeatingStyleId === style.id;
                    return (
                      <div
                        key={style.id}
                        className={`sab-style-card-wrap${
                          isActive ? " is-active" : ""
                        }`}
                      >
                        <button
                          type="button"
                          className={`sab-style-card${
                            isActive ? " is-active" : ""
                          }`}
                          onClick={() => setActiveStyle(style.id)}
                          disabled={isViewer}
                        >
                          <span
                            className={`sab-style-thumb is-${
                              style.isCustom ? "custom" : style.id
                            }`}
                            aria-hidden="true"
                          >
                            <span className="sab-style-thumb-stage" />
                            <span className="sab-style-thumb-seats" />
                          </span>
                          <span className="sab-style-card-copy">
                            <span className="sab-style-card-tag">
                              {style.tag}
                              {isActive ? " · Active" : ""}
                            </span>
                            <strong>{style.name}</strong>
                            <span className="sab-style-card-blurb">
                              {style.blurb}
                            </span>
                          </span>
                        </button>
                        {style.isCustom && !isViewer ? (
                          <div className="sab-style-card-ops">
                            <button
                              type="button"
                              onClick={() => {
                                setEditingStyle(style);
                                setBuilderOpen(true);
                              }}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="is-danger"
                              onClick={() => handleDeleteCustom(style.id)}
                            >
                              Delete
                            </button>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>

                {!catalog.length ? (
                  <p className="sab-people-empty">No layouts available.</p>
                ) : null}
              </section>
            )}
          </>
        )}
        <CapacityNoticeModal
          open={Boolean(capacityNotice)}
          title={capacityNotice?.title}
          message={capacityNotice?.message}
          rows={capacityNotice?.rows}
          hint={capacityNotice?.hint}
          onClose={() => setCapacityNotice(null)}
        />
      </div>
    </div>
  );
};

export default SeatLayoutStudio;
